import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { dataApi } from "../../apis/dataApi";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import LogoCam from '../../assets/Logotipo/Logo_cetesninos_color.png';
import '../../css/bootstrap@5.3.0.min.css';
import '../../css/fonts.css';
import '../../css/style.css';
import bg from '../../assets/Walpapers/Wallpaper.png';

ChartJS.register(ArcElement, Tooltip, Legend);

const options = ['20', '30', '40', '50', '100', '120', '150', '180', '200'];
const optionsTime = ['30', '90', '180', '360'];
const InvestmentRateIDs = [
  'B3421B92-5057-41E8-A9AE-EC71913AD23F',
  '6ED35CBA-4DD3-43B9-BBE9-042E208D1773',
  'ADF109C0-D1EC-4E9E-9C0C-EB1A3504BBF7',
  '5150B758-8C8A-4B84-A116-06B7D4E30815'
];

const porcentages = ['50', '30', '20', '10'];

const generateFolio = () => Math.floor(100000 + Math.random() * 900000).toString();

const obtenerHoraActual = (minutosExtra = 0) => {
  const fecha = new Date();
  fecha.setMinutes(fecha.getMinutes() + minutosExtra);

  let hora = fecha.getHours();
  let minutos = fecha.getMinutes();
  let segundos = fecha.getSeconds();
  let periodo = 'AM';

  // 12h correcto (incluye 12 PM y 12 AM)
  if (hora >= 12) {
    periodo = 'PM';
    if (hora > 12) hora -= 12;
  }
  if (hora === 0) hora = 12;

  const hh = hora < 10 ? `0${hora}` : `${hora}`;
  const mm = minutos < 10 ? `0${minutos}` : `${minutos}`;
  const ss = segundos < 10 ? `0${segundos}` : `${segundos}`;

  return `${hh}:${mm}:${ss} ${periodo}`;
};

const getDate = () => {
  const fecha = new Date();
  const dia = fecha.getDate();
  const mes = fecha.getMonth() + 1;
  const anio = fecha.getFullYear();

  const dd = dia < 10 ? `0${dia}` : `${dia}`;
  const mm = mes < 10 ? `0${mes}` : `${mes}`;

  // Mantengo tu formato: "DD MM YYYY"
  return `${dd} ${mm} ${anio}`;
};

const getPorcentage = (minutos) => {
  if (minutos > 0 && minutos <= 30) return 10;
  if (minutos > 30 && minutos <= 90) return 20;
  if (minutos > 90 && minutos <= 180) return 30;
  if (minutos > 180) return 50;
  return 10;
};

const actualizarInput = (input) => {
  if (input instanceof HTMLElement) {
    const inputMin = Number(input.getAttribute("min"));
    const inputMax = Number(input.getAttribute("max"));
    const unidad = (inputMax - inputMin) / 100;
    input.style.setProperty("--value", (Number(input.value) - inputMin) / unidad);
  }
};

const optionsA = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' }
  }
};

const Registro = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const typeInversion = searchParams.get('t') ?? '';
  const code = searchParams.get('code');
  const amountRaw = searchParams.get('amount');
  const backAccountID = searchParams.get('backAccountID');

  // Entradas (source of truth)
  const [isLoading, setIsLoading] = useState(false);
  const [productInfo] = useState(searchParams.get('productInfo') || '');
  const [establishmentID, setEstablishmentID] = useState('');
  const [facility, setFacility] = useState('');
  const [name, setName] = useState('');

  // ID fijo
  const [id] = useState(generateFolio);

  // Índices (source of truth)
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const [selectedOptionTIndex, setSelectedOptionTIndex] = useState(0);

  // Fecha fija del render inicial
  const [date] = useState(getDate);

  // Hora de inicio: fija (si quieres que se actualice en vivo, te doy variante)
  const [timeInit, setTimeInit] = useState(() => obtenerHoraActual());

  // Normaliza amount a número
  const amount = useMemo(() => {
    const n = Number(amountRaw);
    return Number.isFinite(n) ? n : 0;
  }, [amountRaw]);

  const investmentInit = amountRaw ?? '';

  // Derivados estables con useMemo
  const selectedOption = useMemo(() => Number(options[selectedOptionIndex]), [selectedOptionIndex]);
  const selectedOptionT = useMemo(() => Number(optionsTime[selectedOptionTIndex]), [selectedOptionTIndex]);

  const percentage = useMemo(() => getPorcentage(selectedOptionT), [selectedOptionT]);

  const yieldI = useMemo(() => {
    const val = (selectedOption / 100) * percentage;
    return val.toFixed(2);
  }, [selectedOption, percentage]);

  const total = useMemo(() => {
    const val = selectedOption + Number(yieldI);
    return val.toFixed(2);
  }, [selectedOption, yieldI]);

  const timeEnd = useMemo(() => {
    // Correcto: sumar minutos del plazo, no el índice
    return obtenerHoraActual(selectedOptionT);
  }, [selectedOptionT]);

  const chartData = useMemo(() => {
    return {
      labels: ["Inversión", "Rendimiento", "Total"],
      datasets: [{
        data: [selectedOption, Number(yieldI), Number(total)],
        backgroundColor: ["#717472", "#9f2042", "#c6dcd8"],
        hoverBackgroundColor: ["#717472", "#9f2042", "#c6dcd8"],
        borderWidth: 1,
      }]
    };
  }, [selectedOption, yieldI, total]);

  // Handlers (sin recalcular states derivados)
  const handleInputMove = useCallback((e) => {
    setIsLoading(true);
    const idx = Number(e.target.value);
    setSelectedOptionIndex(idx);
    actualizarInput(e.target);
    setTimeInit(obtenerHoraActual()); // si quieres que el inicio sea "momento del cambio"
    setTimeout(() => setIsLoading(false), 200);
  }, []);

  const handleInputMoveT = useCallback((e) => {
    setIsLoading(true);
    const idx = Number(e.target.value);
    setSelectedOptionTIndex(idx);
    actualizarInput(e.target);
    setTimeout(() => setIsLoading(false), 200);
  }, []);

  const handleClickInicio = useCallback(() => {
    setIsLoading(true);
    navigate('/');
    setTimeout(() => setIsLoading(false), 500);
  }, [navigate]);

  const handleClickContinue = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (typeInversion === 'c') {
        // Validación con números (evita comparaciones string)
        if (selectedOption >= 20 && amount >= selectedOption) {
          const index = porcentages.indexOf(String(percentage));
          const InvestmentRateID = InvestmentRateIDs[index] ?? InvestmentRateIDs[0];

          const tokenResp = await dataApi.getToken();
          if (!tokenResp?.token) throw new Error('No se pudo obtener token');

          const tx = await dataApi.CreateTransaction(
            tokenResp.token,
            selectedOption,
            tokenResp.UserName,
            backAccountID,
            tokenResp.UserID,
            establishmentID,
            facility,
            String(selectedOptionT) // por si API espera string
          );

          //LOG EN JSON
          await fetch('/log', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              backAccountID,
              date1: new Date().toISOString(),
              transactionID: tx.BankAccountTransactionID,
              status: "1",
              description: "Envío de Transferencia - CETES",
              establishmentID,
              facility,
              amount: String(selectedOption),
              date2: new Date().toISOString(),
              userID: tokenResp.UserID,
              userName: tokenResp.UserName,
              selectedOption,
              selectedOptionT
            })
          });

          if (!tx?.isSuccess) throw new Error('Se encontró un error al enviar la cantidad a invertir');

          const inv = await dataApi.InsertInvestment(
            tokenResp.token,
            tx.BankAccountTransactionID,
            InvestmentRateID,
            facility,
            tokenResp.UserName,
            tokenResp.UserID
          );

          if (!inv?.isSuccess) throw new Error('Se encontró un error al enviar el tiempo de inversión');

          toast.info('Se ha enviado la información correctamente');
        } else {
          toast.error('Tienes que tener saldo suficiente e invertir Z 20 o más');
          return;
        }
      }

      // Redirect (en ambos casos) con parámetros consistentes
      const params = new URLSearchParams({
        id,
        date,
        name,
        selectedOption: String(selectedOption),
        percentage: String(percentage),
        yieldI: String(yieldI),
        timeInit,
        timeEnd,
        selectedOptionT: String(selectedOptionT),
        total: String(total),
      });

      navigate('/Detalle?' + params.toString());
    } catch (err) {
      toast.error(err?.message || 'Ocurrió un error al procesar la operación');
    } finally {
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [
    typeInversion,
    selectedOption,
    amount,
    percentage,
    backAccountID,
    establishmentID,
    facility,
    selectedOptionT,
    id,
    date,
    name,
    yieldI,
    timeInit,
    timeEnd,
    total,
    navigate
  ]);

  // Fetch de usuario: SOLO cuando cambie `code`
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const tokenResp = await dataApi.getToken();
        if (!tokenResp?.token) return;

        const responseData = await dataApi.getKidByVisitorId(tokenResp.token, code);
        if (cancelled) return;

        setName(responseData?.name ?? '');
        setEstablishmentID(responseData?.establishmentI ?? '');
        setFacility(responseData?.facility ?? '');
      } catch (e) {
        // opcional: toast / console
      }
    })();

    return () => { cancelled = true; };
  }, [code]);

  // Ajuste visual de sliders: registra una sola vez y limpia
  useEffect(() => {
    const inputs = document.querySelectorAll('input[type=range]');
    inputs.forEach(input => actualizarInput(input));

    const onInput = (evt) => {
      const input = evt.target;
      actualizarInput(input);
    };

    document.addEventListener("input", onInput);
    return () => document.removeEventListener("input", onInput);
  }, []);

  return (
    <div className="cetes-background" style={{ backgroundImage: `url(${bg})` }}>
      <div className="cetes-background-inner">
        <div className="cetes-junior-header">
          <div className="info-header-cetes-junior">
            <div className="text-header-cetes">
              <h4 className="text-header-mayus">{t('summary_title')} <span>{name}</span></h4>
            </div>
            <div className="date-info-sesion">
              <p>{t('summary_hour')}: <span>{timeInit} </span></p>
              <p>{t('summary_date')}: <span>{date} </span></p>
            </div>
          </div>
          <div className="logo_CETES_junior">
            <div className="logo logo-cetes">
              <img src={LogoCam} alt="Logo organización KidZania" />
            </div>
          </div>
        </div>

        <div className="objetivo">
          <div className="objetivo-txt">
            <p>{t('summary_objective')}:</p>
          </div>
          <div className="input-objetivo">
            <form action="">
              <div className="input-txt-cetesJunior">
                <input
                  type="text"
                  name="productInfo"
                  id="productInfo"
                  required
                  value={productInfo}
                  onChange={() => {}}
                  disabled={true}
                />
              </div>
            </form>
          </div>
        </div>

        <div className="reporte-financiero">
          <div className="row justify-content-center gy-5">
            <div className="col-12 col-lg-6 col-xl-4">
              <div className="range-slider-cetes">
                <div className="datos-relevantes-cetes">
                  <div className="item-ttl white-color">
                    <h4>{t('summary_startAmount')}: <span>{investmentInit} kidZos</span></h4>
                  </div>
                  <div className="item-ttl white-color">
                    <h4>{t('summary_rate')}: <span>{percentage}%</span></h4>
                  </div>
                </div>

                <div className="container-resumenGrafico-cetes">
                  <div className="inner-container-resumenGrafico-cetes">

                    <div className="tiempo-range">
                      <div className="titulo-slider-range">
                        <h5>{t('summary_time')}:</h5>
                        <p>{t('summary_minutes')}</p>
                      </div>
                      <div className="wrapper-range">
                        <div className="inner-range">
                          <form>
                            <input
                              value={selectedOptionTIndex}
                              onInput={handleInputMoveT}
                              type="range"
                              id="sliderRangeTime"
                              className="sliderRange-cetes"
                              min={0}
                              max={optionsTime.length - 1}
                              step="1"
                            />
                            <div className="slider-labels" style={{ display: 'flex', justifyContent: 'space-between' }}>
                              {optionsTime.map((label, idx) => (
                                <span key={idx}>{label} ---- </span>
                              ))}
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>

                    <div className="inversion-range">
                      <div className="titulo-slider-range">
                        <h5>{t('summary_investment')}:</h5>
                        <p>{t('summary_amount')}</p>
                      </div>
                      <div className="wrapper-range">
                        <div className="inner-range">
                          <form>
                            <input
                              value={selectedOptionIndex}
                              onInput={handleInputMove}
                              type="range"
                              id="sliderRangeInversion"
                              className="sliderRange-cetes"
                              min={0}
                              max={options.length - 1}
                              step="1"
                            />
                            <div className="slider-labels" style={{ display: 'flex', justifyContent: 'space-between' }}>
                              {options.map((label, idx) => (
                                <span key={idx}>{label} -- </span>
                              ))}
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

              </div>
            </div>

            <div className="col-md-12 col-lg-6 col-xl-8">
              <div className="wrapper-content-second-column">
                <div className="row gy-5">

                  <div className="col-md-6 col-lg-12 col-xl-6">
                    <div className="donut-graphic-cetes">
                      <div className="datos-relevantes-cetes">
                        <div className="item-ttl">
                          <h4>{t('summary_financial')} <span>{t('summary_status')}</span></h4>
                        </div>
                      </div>

                      <div className="container-resumenGrafico-cetes">
                        <div className="simbologia-cuotas" style={{ textAlign: "left" }}>
                          <div className="cuota-item">
                            <div className="cuota-box box-inversion"></div>
                            <div className="text-ttl-inner-resumenGrafico">
                              <p>{t('summary_investment')}:</p>
                            </div>
                            <div className="txt-valor-resumenGrafico">
                              <p>{selectedOption} kidZos</p>
                            </div>
                          </div>

                          <div className="cuota-item">
                            <div className="cuota-box box-rendimiento"></div>
                            <div className="text-ttl-inner-resumenGrafico">
                              <p>{t('symmary_yieldText')}:</p>
                            </div>
                            <div className="txt-valor-resumenGrafico">
                              <p>{yieldI} kidZos</p>
                            </div>
                          </div>

                          <div className="cuota-item">
                            <div className="cuota-box box-total"></div>
                            <div className="text-ttl-inner-resumenGrafico">
                              <p>{t('summary_total')}:</p>
                            </div>
                            <div className="txt-valor-resumenGrafico">
                              <p>{total} kidZos</p>
                            </div>
                          </div>
                        </div>

                        <div style={{ minHeight: 215 }}>
                          <Doughnut data={chartData} options={optionsA} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-12 col-xl-6">
                    <div className="table-resumen-cetes">
                      <div className="datos-relevantes-cetes">
                        <div className="item-ttl">
                          <h4>{t('summary_summary')} <span> {t('summary_operation')}</span> </h4>
                        </div>
                      </div>

                      <div className="container-resumenGrafico-cetes" style={{ textAlign: "left" }}>
                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('summary_id')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{id}</p></div>
                        </div>

                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('summary_date')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{date}</p></div>
                        </div>

                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('summary_name')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{name}</p></div>
                        </div>

                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('summary_investmentAmount')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{selectedOption} kidZos</p></div>
                        </div>

                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('summary_yield')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{percentage}%</p></div>
                        </div>

                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('symmary_yieldText')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{yieldI} kidZos</p></div>
                        </div>

                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('summary_investmentStart')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{timeInit}</p></div>
                        </div>

                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('summary_investmentEnd')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{timeEnd}</p></div>
                        </div>

                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('summary_investmentTime')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{selectedOptionT} {t('summary_minutes')}</p></div>
                        </div>

                        <div className="item-table-resumen">
                          <div className="text-ttl-inner-resumenGrafico"><p>{t('summary_finalBalance')}:</p></div>
                          <div className="txt-valor-resumenGrafico"><p>{total} kidZos</p></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-12 col-xl-12">
                    <div className="btns-reporte">
                      <div className="btn-cetesJunior">
                        <button type="submit" onClick={handleClickInicio}>{t('summary_cancel')}</button>
                      </div>
                      <div className="btn-cetesJunior">
                        <button type="submit" onClick={handleClickContinue}>{t('summary_confirm')}</button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
};

export default Registro;
